import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import TabSwitcherWeb from "@components/switcher/TabSwitcherWeb";
import { billingServices } from "@services/apis/Billing";
import { useEffect, useRef, useState } from "react";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import AffiliateInvoices from "./AffiliateInvoices";
import Billing, { BillingHandle } from "./Billing";
import ConsumerInvoices from "./ConsumerInvoices";
import Expenses from "./Expenses";
import TradeReports from "./TradeReports";

const BillingTabs = () => {
  const { handleError, handleSuccess } = useSnackbarMessage();

  // Track active tab (0: Merchant, 1: Expenses, 2: Consumer, 3: Affiliates)
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const billingRef = useRef<BillingHandle | null>(null);
  const expensesRef = useRef<BillingHandle | null>(null);
  const consumerRef = useRef<BillingHandle | null>(null);
  const affiliatesRef = useRef<BillingHandle | null>(null);

  // Generate monthly billing state (Expenses tab)
  const [month, setMonth] = useState<string>("");
  const [genLoading, setGenLoading] = useState(false);

  const getCurrentMonthYYYYMM = () => {
    const now = new Date();
    const year = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${mm}`;
  };

  useEffect(() => {
    setMonth(getCurrentMonthYYYYMM());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGenerateMonthlyBilling = async () => {
    try {
      setGenLoading(true);

      const selectedMonth = month?.trim()
        ? month.trim()
        : getCurrentMonthYYYYMM();

      await billingServices.generateMonthlyBillingData({
        month: selectedMonth,
      });

      handleSuccess({
        message: `Request processed successfully for ${selectedMonth}`,
      });
    } catch (err: any) {
      handleError({ message: "Error processing the request" });
    } finally {
      setGenLoading(false);
    }
  };

  const tabRefs = [billingRef, expensesRef, consumerRef, affiliatesRef];

  const onExportBillingCSV = async () => {
    const activeRef = tabRefs[activeTabIndex];
    if (!activeRef?.current) {
      handleError({ message: "Table is not ready yet" });
      return;
    }
    try {
      setExportLoading(true);
      await activeRef.current.exportToCSV();
      handleSuccess({ message: "CSV exported successfully" });
    } catch {
      handleError({ message: "Failed to export CSV" });
    } finally {
      setExportLoading(false);
    }
  };

  const TRADE_REPORTS_TAB_INDEX = 4;

  const billingTabs = [
    { label: "Merchant", component: <Billing ref={billingRef} /> },
    { label: "Expenses", component: <Expenses ref={expensesRef} /> },
    { label: "Consumer Invoices", component: <ConsumerInvoices ref={consumerRef} /> },
    { label: "Affiliates", component: <AffiliateInvoices ref={affiliatesRef} /> },
    { label: "Trade Reports", component: <TradeReports /> },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* TOP HEADER */}
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body sticky top-0 z-10 px-5">
        <div className="flex flex-row justify-between items-center h-full">
          <p className="text-text-base text-[14px] font-semibold">Billing</p>

          {/* Right actions - show based on active tab */}
          <div className="flex items-center gap-2">
            {activeTabIndex !== TRADE_REPORTS_TAB_INDEX && (
              <LightCustomButton
                text={exportLoading ? "EXPORTING..." : "EXPORT CSV"}
                onClick={onExportBillingCSV}
                isActive={!exportLoading}
                isButtonLoading={exportLoading}
              />
            )}

            {/* ✅ Expenses tab → Generate Monthly Billing */}
            <>
              <div className="w-[220px]">
                <GlobalTextInputField
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  placeholder="Month (YYYY-MM)"
                  disabled={genLoading}
                  showBorder={true}
                  className="w-full"
                />
              </div>

              <LightCustomButton
                text="GENERATE MONTHLY BILLING"
                onClick={onGenerateMonthlyBilling}
                isActive={!genLoading}
                isButtonLoading={genLoading}
              />
            </>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden mt-4 mx-4">
        <TabSwitcherWeb
          tabs={billingTabs}
          full={true}
          onTabChange={(index: number) => setActiveTabIndex(index)}
        />
      </div>
    </div>
  );
};

export default BillingTabs;
